// K85Report/K85Report.jsx
//
// Візуалізація форми 85-К у кабінеті директора закладу дошкільної освіти.
// Показує звіт «як у Word» без завантаження файлу: тягне дані з /v1/k_85_data
// для власного закладу і підставляє їх у макет офіційної форми.
//
// Дані звіту лежать у k85history.report[КЛЮЧ], де КЛЮЧ — ім'я комірки
// (CHwork10001, PR301, OOP11, AtypeKG1 …). Макет форми (k85form.template.js)
// несе на кожній комірці data-k85="КЛЮЧ", а прапорці Розділу А —
// data-k85chk="КЛЮЧ". Тут ми лише підставляємо значення в готові вузли.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import apiClient, { downloadFile } from '../../../../../services/apiClient';
import K85_FORM_HTML from './k85form.template';
import './k85report.css';

// Місяць-«зріз» для кожного режиму:
//  - 'month' (річний звіт) зберігається під груднем: 12.<рік>
//  - 'day'   (поточний оперативний) — під поточним місяцем: <M>.<рік>
const buildPeriod = (year, mode) => {
    if (mode === 'day') {
        const now = new Date();
        const curYear = now.getFullYear();
        // поточний день-зріз існує лише для поточного року
        const m = (year === curYear) ? (now.getMonth() + 1) : 12;
        return `${m}.${year}`;
    }
    return `12.${year}`;
};

const fetchReport = async ({ kindergartenId, year, mode }) => {
    const period = buildPeriod(year, mode);
    const params = new URLSearchParams({
        kindergartenId,
        type: mode,       // 'month' | 'day'
        period
    });
    const res = await apiClient.get(`k_85_data?${params.toString()}`);
    // responseHandler.data → { data: [ k85history-doc, ... ] }
    const doc = Array.isArray(res?.data) ? res.data[0] : null;
    return doc?.report || null;
};

// Перерахунок звіту на бекенді (POST /v1/k_85_data → saveK85_1):
// getK85Body рахує СВІЖІ дані (діти, групи, employees) станом на «зараз» і
// зберігає в k85histories під ключем { type, month }. Компонент читає це
// збереження через fetchReport. Перерахунок актуальний лише для поточного
// року — минулі роки лишаються збереженими знімками.
const recalcReport = async ({ kindergartenId, year, mode }) => {
    const period = buildPeriod(year, mode);
    // month-режим зберігається під type:'month' (needMonth:true),
    // day-режим — під type:'day' (за замовчуванням у saveK85_1).
    const body = {
        kindergartenId,
        month: period,
        needMonth: mode === 'month',
        need_jan: false
    };
    // POST на той самий /v1/k_85_data; помилку перерахунку не робимо
    // фатальною — далі все одно спробуємо прочитати те, що є.
    await apiClient.post('k_85_data', body);
};

// Форматуємо число звіту: 0 показуємо як 0, порожнє — як пусто (не "undefined").
const fmt = (v) => {
    if (v === null || v === undefined || v === '') return '';
    return String(v);
};

const K85Report = ({ kindergartenId, defaultYear, defaultMode = 'month' }) => {
    const containerRef = useRef(null);

    const currentYear = new Date().getFullYear();
    const [year, setYear] = useState(defaultYear || currentYear);
    const [mode, setMode] = useState(defaultMode); // 'month' річний | 'day' поточний
    const [report, setReport] = useState(null);
    const [status, setStatus] = useState('idle');  // idle | loading | ready | empty | error
    const [errorMsg, setErrorMsg] = useState('');

    const load = useCallback(async () => {
        if (!kindergartenId) return;
        setStatus('loading');
        setErrorMsg('');
        try {
            // Перед читанням — перерахунок на бекенді, щоб врахувати свіжі зміни
            // (діти, групи, працівники). Тільки для поточного року: минулі роки
            // зберігаються як знімки й перерахунку «на зараз» не підлягають.
            if (year === currentYear) {
                try {
                    await recalcReport({ kindergartenId, year, mode });
                } catch (e) {
                    // перерахунок не критичний — читаємо те, що збережено
                }
            }
            const rep = await fetchReport({ kindergartenId, year, mode });
            if (!rep || Object.keys(rep).length === 0) {
                setReport(null);
                setStatus('empty');
                return;
            }
            setReport(rep);
            setStatus('ready');
        } catch (err) {
            setReport(null);
            setStatus('error');
            setErrorMsg(err?.message || 'Не вдалося завантажити звіт');
        }
    }, [kindergartenId, year, mode, currentYear]);

    useEffect(() => { load(); }, [load]);

    // Підстановка значень у макет після кожного рендера готового звіту.
    useEffect(() => {
        const root = containerRef.current;
        if (!root || status !== 'ready' || !report) return;

        // 1) Комірки даних: <td data-k85="CHwork10001">
        root.querySelectorAll('[data-k85]').forEach((node) => {
            const key = node.getAttribute('data-k85');
            const val = fmt(report[key]);
            node.textContent = val === '' ? '\u00A0' : val;
        });

        // 2) Прапорці Розділу А: показуємо ✓, коли значення truthy.
        root.querySelectorAll('[data-k85chk]').forEach((node) => {
            const key = node.getAttribute('data-k85chk');
            const raw = report[key];
            const checked = raw === true || raw === 1 || raw === '1' ||
                (typeof raw === 'string' && raw.trim() !== '' && raw.trim() !== '0');
            node.textContent = checked ? '✓' : '';
            node.classList.toggle('is-checked', checked);
        });
    }, [report, status]);

    // ── Експорт у PDF ────────────────────────────────────────────────────────
    // Друкуємо ЛИШЕ аркуш форми (без меню/навігації застосунку) через ізольований
    // прихований iframe з власними @page-правилами A4. Текст лишається текстом
    // (не растр), пагінація коректна. Користувач у діалозі друку обирає
    // «Зберегти як PDF».
    const exportPdf = useCallback(() => {
        const src = containerRef.current;
        if (!src) return;

        const iframe = document.createElement('iframe');
        iframe.setAttribute('aria-hidden', 'true');
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = '0';
        document.body.appendChild(iframe);

        const doc = iframe.contentWindow.document;

        // Переносимо стилі форми: усі <style> та <link rel=stylesheet> з головного
        // документа, щоб таблиці й межі виглядали 1:1 як на екрані.
        const headStyles = Array.from(
            document.querySelectorAll('style, link[rel="stylesheet"]')
        ).map((n) => n.outerHTML).join('\n');

        const printCss = `
            <style>
                @page { size: A4 landscape; margin: 10mm; }
                html, body { margin: 0; padding: 0; background: #fff; }
                .k85-report { min-width: 0 !important; max-width: none !important; margin: 0 !important; }
                /* не розривати рядки таблиць між сторінками */
                .k85-report tr, .k85-report h1 { break-inside: avoid; page-break-inside: avoid; }
                .k85-report h1 { break-after: avoid; page-break-after: avoid; }
            </style>`;

        doc.open();
        doc.write(`<!doctype html><html><head><meta charset="utf-8">${headStyles}${printCss}</head><body><div class="k85-report">${src.innerHTML}</div></body></html>`);
        doc.close();

        const finish = () => {
            try {
                iframe.contentWindow.focus();
                iframe.contentWindow.print();
            } finally {
                // прибираємо iframe трохи згодом, коли діалог друку вже відкрито
                setTimeout(() => document.body.removeChild(iframe), 1000);
            }
        };

        // даємо браузеру домалювати вміст перед друком
        if (iframe.contentWindow.document.readyState === 'complete') {
            setTimeout(finish, 150);
        } else {
            iframe.contentWindow.onload = () => setTimeout(finish, 150);
        }
    }, []);

    // ── Завантаження DOCX ────────────────────────────────────────────────────
    // Бекенд формує справжній Word-файл із того самого шаблону
    // 85_k_18_template_2025.docx (ендпоінт /v1/get_k85). Тягнемо як blob
    // із токеном авторизації та зберігаємо.
    const [docxBusy, setDocxBusy] = useState(false);
    const exportDocx = useCallback(async () => {
        if (!kindergartenId) return;
        setDocxBusy(true);
        try {
            const params = new URLSearchParams({ kg_id: kindergartenId });
            const res = await downloadFile(`get_k85?${params.toString()}`);
            if (res && !res.ok) {
                alert('Не вдалося сформувати DOCX. Спробуйте ще раз пізніше.');
            }
        } catch (e) {
            alert('Не вдалося сформувати DOCX: ' + (e?.message || 'помилка'));
        } finally {
            setDocxBusy(false);
        }
    }, [kindergartenId]);

    const years = [];
    for (let y = currentYear; y >= currentYear - 6; y--) years.push(y);

    return (
        <div className="k85-report-wrap">
            <div className="k85-toolbar no-print">
                <div className="k85-toolbar__title">
                    Звіт за формою 85-К
                </div>

                <div className="k85-toolbar__controls">
                    <label className="k85-field">
                        <span>Рік</span>
                        <select
                            value={year}
                            onChange={(e) => setYear(+e.target.value)}
                            disabled={status === 'loading'}
                        >
                            {years.map((y) => <option key={y} value={y}>{y}</option>)}
                        </select>
                    </label>

                    <div className="k85-modeswitch" role="group" aria-label="Режим звіту">
                        <button
                            type="button"
                            className={mode === 'month' ? 'is-active' : ''}
                            onClick={() => setMode('month')}
                            disabled={status === 'loading'}
                        >
                            Річний
                        </button>
                        <button
                            type="button"
                            className={mode === 'day' ? 'is-active' : ''}
                            onClick={() => setMode('day')}
                            disabled={status === 'loading'}
                        >
                            Поточний
                        </button>
                    </div>

                    <button
                        type="button"
                        className="k85-btn k85-btn--ghost"
                        onClick={load}
                        disabled={status === 'loading'}
                    >
                        Оновити
                    </button>

                    <button
                        type="button"
                        className="k85-btn"
                        onClick={exportPdf}
                        disabled={status !== 'ready'}
                    >
                        Зберегти PDF
                    </button>

                    <button
                        type="button"
                        className="k85-btn k85-btn--word"
                        onClick={exportDocx}
                        disabled={status === 'loading' || docxBusy}
                        title="Завантажити офіційний Word-файл форми 85-К"
                    >
                        {docxBusy ? 'Формування…' : 'Завантажити DOCX'}
                    </button>
                </div>
            </div>

            {status === 'loading' && (
                <div className="k85-state">Формування звіту за актуальними даними…</div>
            )}

            {status === 'empty' && (
                <div className="k85-state k85-state--empty">
                    Звіт за {mode === 'month' ? 'рік' : 'поточний період'} {year} ще не сформовано.
                    Дані з’являться після формування звіту за обраний період.
                </div>
            )}

            {status === 'error' && (
                <div className="k85-state k85-state--error">
                    Не вдалося завантажити звіт: {errorMsg}
                    <button type="button" className="k85-btn k85-btn--ghost" onClick={load}>
                        Спробувати ще раз
                    </button>
                </div>
            )}

            {status === 'ready' && (
                <div className="k85-report-scroll">
                    <div
                        className="k85-report"
                        ref={containerRef}
                        dangerouslySetInnerHTML={{ __html: K85_FORM_HTML }}
                    />
                </div>
            )}
        </div>
    );
};

export default K85Report;